<template lang="pug">
VCard
  VCardSubtitle.my-4 {{ book.name }}
  VCardTitle.text-h1.overlow-visible(:class="{ 'text-primary': !dark }") {{ abbreviation }}
  VCardSubtitle.mb-4
    span(
      v-html="i18n.t('ScriptureIndex.comments', { count: bookCountData.countHtml }, bookCountData.countTotal)"
    )
  VCardActions
    VBtn(
      :to="byChapterAction ? undefined : localePath(getScriptureIndexChaptersLocation(book.bid))"
      :color="dark ? 'primary-c050' : 'primary-c600'"
      variant="outlined"
      @click="onClickChapter"
    ) {{ i18n.t("ScriptureIndex.ByChapter") }}
    IndexStudyMenu(
      :commentary-types-counts="index.commentaryTypesCounts"
      :range="book"
      :count="index.count"
      :count-locale="index.languagesCounts[locale] ?? 0"
      :other-languages="otherLanguages"
    )
</template>

<script setup lang="ts">
import {
  bookAbbreviationsPerLanguage,
  type BookId,
  type StandaloneBookData,
} from "@rsc/scripture-model";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { CommentaryIndexRangeCount } from "~/units/commentary/commentaryIndex";
import { getScriptureIndexChaptersLocation } from "~/routing/locations";
import { type CountData, createCountHtml } from "./CommentaryType.util";
import { allTranslations } from "~/static/translations";
import { useDarkMode } from "~/composables/useDarkMode";

const localePath = useLocalePath();

interface Props {
  index: CommentaryIndexRangeCount<StandaloneBookData>;
  byChapterAction?: (bid: BookId) => void;
  otherLanguages: string[];
}
const i18n = useI18n();
const locale = i18n.locale;
const dark = useDarkMode();
const props = defineProps<Props>();

const book = computed(() => props.index.range);
const abbreviation = computed(
  () =>
    bookAbbreviationsPerLanguage[
      allTranslations[book.value.tid]?.language ?? "en"
    ][book.value.bid][0] ?? book.value.name,
);

const bookCountData = computed<CountData>(() => {
  const countTotal: number = props.index.count;
  const countLocal: number =
    props.index.languagesCounts[i18n.locale.value] ?? 0;

  const maxCountOther = props.otherLanguages.reduce(
    (acc: null | number, currentOtherLocale: string) => {
      const count = props.index.languagesCounts[currentOtherLocale] ?? 0;
      return Math.max(count, acc ?? 0);
    },
    null,
  );

  return {
    countTotal,
    countLocal,
    maxCountOther,
    countHtml: createCountHtml({ countLocal, maxCountOther }),
  };
});

function onClickChapter() {
  if (props.byChapterAction) {
    props.byChapterAction(book.value.bid);
  }
}
</script>
